import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {  Breadcrumbs, Typography , Box, IconButton, Link as MuiLink} from "@mui/material";
import { Home, ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import { useSession } from '../contexts/SessionContext';
import { Icon as OpusIcon } from "./common/opus";

// Single source of truth for breadcrumb typography so every page matches:
// leaf = bold primary, parent (link) = regular secondary, same size + line-height.
// OPUS-913: match the design PageHeader title — prominent 1.25rem/600 leaf.
const LEAF_SX = { color: "text.primary", fontSize: "1.25rem", lineHeight: 1.2, fontWeight: 600 };
const PARENT_SX = { color: "text.secondary", fontSize: "1.25rem", lineHeight: 1.2, fontWeight: 400 };

/**
 * Pages should prefer the `trail` prop — an array of { label, to?, onClick? }.
 * The last item renders as the bold leaf; earlier items render as muted links.
 * `breadcrumbContent` is kept for pages whose breadcrumb embeds interactive
 * controls (e.g. the caption editor's dropdowns).
 */
const DashboardBreadcrumbs = ({ rightAction, breadcrumbContent, trail }) => {
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState('')
  const { isDrawerOpen, toggleDrawer } = useSession();

  useEffect(() => {
    const title = location.pathname.split('/').filter(Boolean)
    setPageTitle(title[title.length - 1])
  }, [location])

  const renderTrail = () =>
    trail.map((c, i) => {
      const last = i === trail.length - 1;
      if (!last && (c.to || c.onClick)) {
        return (
          <MuiLink
            key={i}
            component={c.to ? RouterLink : "button"}
            to={c.to}
            type={c.to ? undefined : "button"}
            underline="hover"
            onClick={c.onClick}
            sx={{ ...PARENT_SX, cursor: "pointer", background: "none", border: "none", p: 0 }}
          >
            {c.label}
          </MuiLink>
        );
      }
      return (
        <Typography key={i} component="span" sx={last ? LEAF_SX : PARENT_SX}>
          {c.label}
        </Typography>
      );
    });

  return (
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    mt: 0,
    mb: 0,
    py: 0,
    gap: 0.75,
    // OPUS-1045: fixed row height so the breadcrumb is the same size/baseline
    // whether or not a page passes a rightAction button (the ~38px action used
    // to make the row taller on some pages and shorter on others).
    minHeight: 44,
  }}
>
  <IconButton
    size="small"
    sx={{ width: 36, height: 36, mr: 0.5, borderRadius: "8px", background: "var(--panel)", color: "#fff", "&:hover": { background: "var(--surface-3)" } }}
    onClick={() => toggleDrawer(!isDrawerOpen)}
  >
    {!isDrawerOpen ? <OpusIcon name="chevronRight" size={18} /> : <OpusIcon name="chevronLeft" size={18} />}
  </IconButton>

  <Breadcrumbs
    aria-label="breadcrumb"
    sx={{
      fontSize: "1.25rem",
      lineHeight: 1.2,
      pl: 0,
      pt: 0,
      "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "var(--text-dim)" },
      "& .MuiBreadcrumbs-li": { display: "flex", alignItems: "center" },
      "& .MuiBreadcrumbs-ol": { alignItems: "center" },
    }}
  >
    <RouterLink underline="none" to="/dashboard" style={{ display: "flex", alignItems: "center", color: "#fff" }}>
      <OpusIcon name="home" size={20} />
    </RouterLink>
    {Array.isArray(trail) && trail.length
      ? renderTrail()
      : breadcrumbContent != null
        ? breadcrumbContent
        : (pageTitle && (
          <Typography sx={LEAF_SX}>
            {/* Title-case every word (split on ALL hyphens), so e.g.
                "scene-management" → "Scene Management", not "Scene management". */}
            {pageTitle
              .split("-")
              .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
              .join(" ")}
          </Typography>
        ))}
  </Breadcrumbs>

  {rightAction && (
    <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
      {rightAction}
    </Box>
  )}
</Box>

  );
};

export default DashboardBreadcrumbs;
